import React, { useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faInfoCircle, faTimes } from '@fortawesome/free-solid-svg-icons';
import { theme } from '../../styles/theme';

const BannerContainer = styled.div`
  position: fixed;
  top: 180px; /* Position below announcement banner (27px) + navbar (120px) + some spacing */
  left: 0;
  right: 0;
  background: ${props => props.theme.colors.offWhite};
  color: ${props => props.theme.colors.primary};
  padding: 1rem 2rem;
  z-index: 100;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  border-bottom: 2px solid ${props => props.theme.colors.accent};
  display: flex;
  align-items: center;
  justify-content: center;
  animation: slideDown 0.5s ease-out forwards;
  
  @keyframes slideDown {
    from {
      transform: translateY(-100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @media (max-width: 1024px) {
    top: 160px;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileInfoButton = styled.button`
  display: none;
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: ${props => props.theme.colors.offWhite};
  border: 2px solid ${props => props.theme.colors.accent};
  color: ${props => props.theme.colors.primary};
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 1000;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const ModalOverlay = styled.div`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1001;
  animation: fadeIn 0.3s ease-out forwards;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    display: ${props => props.$isOpen ? 'block' : 'none'};
  }
`;

const ModalContent = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  padding: 1.5rem;
  border-radius: 20px 20px 0 0;
  transform: translateY(${props => props.$isOpen ? '0' : '100%'});
  transition: transform 0.3s ease-out;
  z-index: 1002;

  @media (max-width: 768px) {
    display: block;
  }
`;

const BannerContent = styled.div`
  max-width: 1200px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    gap: 0.5rem;
  }
`;

const InfoSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const Icon = styled.div`
  font-size: 1.2rem;
  color: ${props => props.theme.colors.accent};
`;

const TextContent = styled.div`
  font-size: 1rem;
  line-height: 1.4;
  
  p {
    margin: 0;
  }
`;

const StatusText = styled.span`
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatusIndicator = styled.span`
  color: ${props => {
    if (props.$isOpen) return props.theme.colors.success;
    if (props.$openingSoon) return props.theme.colors.warning;
    return props.theme.colors.error;
  }};
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.primary};
  cursor: pointer;
  font-size: 1rem;
  padding: 0.5rem;
  transition: opacity 0.2s ease;
  flex-shrink: 0;
  
  &:hover {
    opacity: 0.8;
  }
`;

const OpeningStatusBanner = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const getStatusText = (status) => {
    if (status.isOpen) return 'Open';
    if (status.openingSoon) return 'Opening Soon';
    return 'Closed';
  };

  // Default opening hours
  const openingHours = {
    castle: {
      days: "Mon - Sun",
      times: "10:00 - 17:00",
      note: ""
    },
    gardens: {
      days: "Mon - Sun",
      times: "09:30 - 17:00",
      note: ""
    },
    retail: {
      days: "Mon - Sun",
      times: "09:00 - 17:00",
      note: ""
    }
  };

  // Get current day of week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
  const currentDay = new Date().getDay();
  const isWeekend = currentDay === 0 || currentDay === 6; // 0 = Sunday, 6 = Saturday
  
  // Check if today is May 5th, 2025
  const today = new Date();
  const isMay5th2025 = today.getFullYear() === 2025 && 
                       today.getMonth() === 4 && // May is month 4 (0-based)
                       today.getDate() === 5;
  
  // Check if today is May 29th or 30th, 2025
  const isMay29thOr30th2025 = today.getFullYear() === 2025 && 
                             today.getMonth() === 4 && // May is month 4 (0-based)
                             (today.getDate() === 29 || today.getDate() === 30);
  
  // Check if today is during the private family event closure (June 16th-23rd, 2025)
  const isPrivateEventClosure = today.getFullYear() === 2025 &&
                               today.getMonth() === 5 && // June is month 5 (0-based)
                               today.getDate() >= 16 && today.getDate() <= 23;

  // Check if today is October 16th, 2025 (castle closed for private event)
  const isOctober16thClosure = today.getFullYear() === 2025 &&
                               today.getMonth() === 9 && // October is month 9 (0-based)
                               today.getDate() === 16;
  
  // Animal Park status - closed until further notice
  const animalParkStatus = (() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    const day = today.getDate();
    
    // Animal Park closed on December 31st, 2025
    if (year === 2025 && month === 11 && day === 31) {
      return { isOpen: false, openingSoon: false };
    }
    
    // Animal Park closed until further notice (stays closed in 2026)
    return { isOpen: false, openingSoon: false };
  })();

  // Function to check if a venue is open today (regardless of current time)
  const isVenueOpenToday = (venue, venueType) => {
    if (!venue || !venue.times) return { isOpen: false, openingSoon: false };
    
    // Get current date
    const currentDate = new Date();
    const day = currentDate.getDate();
    const month = currentDate.getMonth();
    const year = currentDate.getFullYear();
    const dayOfWeek = currentDate.getDay(); // 0 = Sunday, 1 = Monday, etc.

    // Check special closure dates
    if (year === 2025) {
      // Christmas Day - All closed
      if (month === 11 && day === 25) {
        return { isOpen: false, openingSoon: false };
      }
      
      // Boxing Day (Dec 26) - Gardens and Playground only, 9:30am-3:30pm
      if (month === 11 && day === 26) {
        if (venueType === 'castle' || venueType === 'retail') {
          return { isOpen: false, openingSoon: false };
        }
        // Gardens open with special hours (9:30am-3:30pm)
        return { isOpen: true, openingSoon: false };
      }
      
      // December 31st, 2025 - Castle closed, Gardens and Retail open
      if (month === 11 && day === 31) {
        if (venueType === 'castle') {
          return { isOpen: false, openingSoon: false };
        }
        // Gardens and Retail open
        return { isOpen: true, openingSoon: false };
      }

      // Castle specific dates
      if (venueType === 'castle') {
        // Castle closed until November 29th, 2025
        if (month < 10 || (month === 10 && day < 29)) { // Before November 29th
          return { isOpen: false, openingSoon: false };
        }
        
        // Castle Christmas Experience: Nov 29 - Dec 30, 2025 (10am-5pm daily)
        if (month === 10 && day >= 29) { // November 29th onwards
          return { isOpen: true, openingSoon: false };
        }
        
        // December 1-30, 2025 - Castle Christmas Experience
        if (month === 11 && day >= 1 && day <= 30) {
          return { isOpen: true, openingSoon: false };
        }
        
        // December 31st, 2025 - Castle closed
        if (month === 11 && day === 31) {
          return { isOpen: false, openingSoon: false };
        }
        
        // April private event (24th-27th) - Castle closed
        if (month === 3 && (day >= 24 && day <= 27)) {
          return { isOpen: false, openingSoon: false };
        }
        
        // April 29th and 30th - Castle closed
        if (month === 3 && (day === 29 || day === 30)) {
          return { isOpen: false, openingSoon: false };
        }
        
        // May closures
        if (month === 4) { // May
          // Specific dates
          if (day === 6 || day === 7 || day === 13 || day === 14 || day === 20 || day === 21 || day === 27 || day === 28) {
            return { isOpen: false, openingSoon: false };
          }
        }
        // June closures
        if (month === 5) { // June
          // Private family event closure: June 16th-23rd, 2025
          if (day >= 16 && day <= 23) {
            return { isOpen: false, openingSoon: false };
          }
        }
        // July 20th, 2025 - Castle closed
        if (month === 6 && day === 20) { // July
          return { isOpen: false, openingSoon: false };
        }
        // October closures
        if (month === 9) { // October
          // October 16th, 2025 - Castle closed for private event
          if (day === 16) return { isOpen: false, openingSoon: false };
        }
      }

      // Gardens specific closures
      if (venueType === 'gardens') {
        // Private family event closure: June 16th-23rd, 2025
        if (month === 5 && day >= 16 && day <= 23) { // June
          return { isOpen: false, openingSoon: false };
        }
        // July 20th, 2025 - Gardens closed
        if (month === 6 && day === 20) { // July
          return { isOpen: false, openingSoon: false };
        }
      }

      // Retail specific closures
      if (venueType === 'retail') {
        // Private family event closure: Sunday June 22nd, 2025
        if (month === 5 && day === 22) { // June 22nd
          return { isOpen: false, openingSoon: false };
        }
      }
    }
    
    // Check 2026 dates
    if (year === 2026) {
      // New Year's Day (Jan 1) - Gardens and Playground only, 9:30am-3:30pm
      if (month === 0 && day === 1) {
        if (venueType === 'castle' || venueType === 'retail') {
          return { isOpen: false, openingSoon: false };
        }
        // Gardens open with special hours (9:30am-3:30pm)
        return { isOpen: true, openingSoon: false };
      }
      
      // January 2nd to March 14, 2026 - Castle closed, Gardens & Retail open daily
      if ((month === 0 && day >= 2) || (month === 1) || (month === 2 && day < 14)) {
        if (venueType === 'castle') {
          return { isOpen: false, openingSoon: false };
        }
        // Gardens and Retail open daily
        return { isOpen: true, openingSoon: false };
      }
      
      // March 14, 2026 onwards - Castle reopens
      if (month > 2 || (month === 2 && day >= 14)) {
        if (venueType === 'castle') {
          // Castle closed on Tuesdays and Wednesdays
          const dayOfWeek = currentDate.getDay();
          if (dayOfWeek === 2 || dayOfWeek === 3) {
            return { isOpen: false, openingSoon: false };
          }
        }
      }
    }
    
    // If we've reached this point, the venue is open today
    return { isOpen: true, openingSoon: false };
  };

  // Calculate status for each venue
  const castleStatus = isVenueOpenToday(openingHours.castle, 'castle');
  const gardensStatus = isVenueOpenToday(openingHours.gardens, 'gardens');
  const retailStatus = isVenueOpenToday(openingHours.retail, 'retail');

  if (!isVisible) return null;

  return (
    <>
      <BannerContainer>
        <BannerContent>
          <InfoSection>
            <Icon>
              <FontAwesomeIcon icon={faClock} />
            </Icon>
            <TextContent>
              <p>
                <StatusText>Today's Opening Status:</StatusText>{' '}
                <StatusIndicator $isOpen={castleStatus.isOpen} $openingSoon={castleStatus.openingSoon}>
                  Castle: {getStatusText(castleStatus)}
                </StatusIndicator>{' '}
                •{' '}
                <StatusIndicator $isOpen={gardensStatus.isOpen} $openingSoon={gardensStatus.openingSoon}>
                  Gardens: {getStatusText(gardensStatus)}
                </StatusIndicator>{' '}
                •{' '}
                <StatusIndicator $isOpen={retailStatus.isOpen} $openingSoon={retailStatus.openingSoon}>
                  Retail: {getStatusText(retailStatus)}
                </StatusIndicator>{' '}
                •{' '}
                <StatusIndicator $isOpen={animalParkStatus.isOpen} $openingSoon={animalParkStatus.openingSoon}>
                  Animal Park: {getStatusText(animalParkStatus)}
                </StatusIndicator>
              </p>
              <p style={{ fontSize: '0.9rem', marginTop: '0.3rem', color: theme.colors.accent, fontWeight: '500' }}>
                Exclusive Belvoir Group Saver when you book online
              </p>
              {/* <p style={{ fontSize: '0.85rem', marginTop: '0.2rem', color: theme.colors.primary, fontStyle: 'italic' }}>
                Animal Park: Closed weekdays from 31st August 2025 • Castle: Closed Tuesday & Wednesday
              </p> */}
            </TextContent>
          </InfoSection>
          <CloseButton onClick={() => setIsVisible(false)}>
            <FontAwesomeIcon icon={faTimes} />
          </CloseButton>
        </BannerContent>
      </BannerContainer>

      <MobileInfoButton onClick={() => setIsModalOpen(true)}>
        <FontAwesomeIcon icon={faClock} />
      </MobileInfoButton>

      <ModalOverlay $isOpen={isModalOpen} onClick={() => setIsModalOpen(false)}>
        <ModalContent $isOpen={isModalOpen} onClick={e => e.stopPropagation()}>
          <BannerContent>
            <InfoSection>
              <Icon>
                <FontAwesomeIcon icon={faClock} />
              </Icon>
              <TextContent>
                <p>
                  <StatusText>Today's Opening Status:</StatusText>{' '}
                  <StatusIndicator $isOpen={castleStatus.isOpen} $openingSoon={castleStatus.openingSoon}>
                    Castle: {getStatusText(castleStatus)}
                  </StatusIndicator>{' '}
                  •{' '}
                  <StatusIndicator $isOpen={gardensStatus.isOpen} $openingSoon={gardensStatus.openingSoon}>
                    Gardens: {getStatusText(gardensStatus)}
                  </StatusIndicator>{' '}
                  •{' '}
                  <StatusIndicator $isOpen={retailStatus.isOpen} $openingSoon={retailStatus.openingSoon}>
                    Retail: {getStatusText(retailStatus)}
                  </StatusIndicator>{' '}
                  •{' '}
                  <StatusIndicator $isOpen={animalParkStatus.isOpen} $openingSoon={animalParkStatus.openingSoon}>
                    Animal Park: {getStatusText(animalParkStatus)}
                  </StatusIndicator>
                </p>
                <p style={{ fontSize: '0.9rem', marginTop: '0.3rem', color: theme.colors.accent, fontWeight: '500' }}>
                  Exclusive Belvoir Group Saver when you book online
                </p>
                {/* <p style={{ fontSize: '0.85rem', marginTop: '0.2rem', color: theme.colors.primary, fontStyle: 'italic' }}>
                  Animal Park: Closed weekdays from 31st August 2025 • Castle: Closed Tuesday & Wednesday
                </p> */}
              </TextContent>
            </InfoSection>
            <CloseButton onClick={() => setIsModalOpen(false)}>
              <FontAwesomeIcon icon={faTimes} />
            </CloseButton>
          </BannerContent>
        </ModalContent>
      </ModalOverlay>
    </>
  );
};

export default OpeningStatusBanner; 